import { useDrawer } from '../context/DrawerContext';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import useMyCarsModalOpen from '../hooks/useMyCarsModalOpen';

/**
 * DrawerScrollLock — owns page scroll-lock for the whole drawer family.
 *
 * Renders nothing; it only decides when the page should be scroll-locked.
 * Rendered once inside DrawerNavigationProvider, since that's the only scope
 * that can see both the main-panel open state and the drilldown state.
 *
 * Scroll stays locked as long as ANY of the three drawer surfaces is open:
 * the main panel, an active drilldown, or My Cars' own modal
 * (`isOpen || activeDrillDownItem !== null || isMyCarsModalOpen`). Combining
 * all three into one lock is what stops a double-toggle bug: without it,
 * closing a drilldown while the main panel is still open would unlock scroll
 * even though the drawer is still on screen.
 *
 * `isMyCarsModalOpen` always reflects ws-mycars-nav's own confirmed
 * open/closed events -- never our own request to open its modal, because that
 * request can be silently dropped (e.g. before its listener has attached),
 * which would leave scroll locked with nothing left to unlock it.
 *
 * react-bootstrap's ModalManager used to measure the scrollbar width and
 * compensate for it automatically. Now that it's gone, `useBodyScrollLock`
 * does that measurement itself, so nothing here has to.
 *
 * @returns {null}
 */
const DrawerScrollLock = () => {
	const { isOpen } = useDrawer();
	const { activeDrillDownItem } = useDrawerNavigation();
	const isMyCarsModalOpen = useMyCarsModalOpen();

	useBodyScrollLock(
		isOpen || activeDrillDownItem !== null || isMyCarsModalOpen
	);

	return null;
};

export { DrawerScrollLock };
